

import React from 'react';
import { Ticket, TicketStatus, PaymentStatus, RepairStageStatus, AppSettings, Employee, EmployeeRole } from '../types'; 
import { PrintableDeviceLabel } from './PrintableDeviceLabel'; 

interface RecentRepairsTableProps {
  tickets: Ticket[];
  appSettings: AppSettings; 
  currentUser: Employee | null; // Added currentUser
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile: (customerName: string) => void;
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void; 
  onShowCustomerPortal?: (ticketId: string) => void;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void; 
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void; 
}

export const RecentRepairsTable: React.FC<RecentRepairsTableProps> = ({ 
    tickets, 
    appSettings,
    currentUser,
    onOpenUpdateStatusModal, 
    onViewCustomerProfile, 
    onOpenPaymentModal, 
    onOpenPrintModal, 
    onShowCustomerPortal,
}) => {
  if (tickets.length === 0) {
    return <p className="text-slate-500 text-center py-4">No recent repairs to display.</p>;
  }

  const isCurrentUserInactive = currentUser?.status === 'Inactive';
  const canCurrentUserProcessPayment = currentUser?.role !== 'Technician' && !isCurrentUserInactive;


  const getStatusClass = (status: TicketStatus): string => {
    switch (status) {
      case TicketStatus.NEW:
        return 'bg-sky-100 text-sky-700';
      case TicketStatus.IN_PROGRESS:
        return 'bg-amber-100 text-amber-700';
      case TicketStatus.WAITING_FOR_PARTS:
        return 'bg-purple-100 text-purple-700';
      case TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL:
        return 'bg-orange-100 text-orange-700';
      case TicketStatus.READY_FOR_PICKUP:
        return 'bg-[var(--color-accent)] bg-opacity-80 text-black';
      case TicketStatus.COMPLETED:
        return 'bg-emerald-100 text-emerald-700';
      case TicketStatus.CUSTOMER_DECLINED:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA'); 
  };

  const TagIconMini: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" transform="scale(0.66)" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" transform="scale(0.66)" />
    </svg>
  );

  const EyeIconMini: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5 mr-0.5" {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" transform="scale(0.66) translate(4,4)" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" transform="scale(0.66) translate(4,4)" />
    </svg>
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Device
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Issue
            </th>
             <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Amount Due
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Date
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {tickets.map((ticket) => {
            const canTakePayment = (
              ticket.status === TicketStatus.READY_FOR_PICKUP || 
              ticket.status === TicketStatus.COMPLETED ||
              (ticket.status === TicketStatus.IN_PROGRESS && ticket.isEstimateApproved === true)
            ) && ticket.paymentStatus === PaymentStatus.UNPAID && ticket.amountDue > 0;
            
            return (
              <tr key={ticket.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                  <button 
                    onClick={() => onViewCustomerProfile(ticket.customer)} 
                    className="text-[var(--color-primary)] hover:brightness-90 hover:underline font-medium transition-colors"
                    aria-label={`View profile for ${ticket.customer}`}
                  >
                    {ticket.customer}
                  </button>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{ticket.deviceModel || 'N/A'}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700 truncate max-w-xs" title={ticket.subject}>
                  {ticket.subject}
                </td>
                 <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                  ${ticket.amountDue.toFixed(2)}
                  {ticket.paymentStatus === PaymentStatus.PAID && <span className="ml-1 text-xs text-emerald-600">(Paid)</span>}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">{formatDate(ticket.createdAt)}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm space-x-2">
                  <button
                    onClick={() => onOpenUpdateStatusModal(ticket.id, ticket.status)}
                    className={`font-medium transition-colors ${isCurrentUserInactive ? 'text-slate-400 cursor-not-allowed' : 'text-[var(--color-primary)] hover:brightness-90'}`}
                    aria-label={`Update status for ticket ${ticket.id}`}
                    disabled={isCurrentUserInactive}
                    title={isCurrentUserInactive ? "Inactive users cannot update status" : undefined}
                  >
                    Update
                  </button>
                  {canTakePayment && (
                     <button
                        onClick={() => onOpenPaymentModal(ticket.id, ticket.amountDue, ticket.customer)}
                        className={`font-medium transition-colors ${!canCurrentUserProcessPayment ? 'text-slate-400 cursor-not-allowed' : 'text-emerald-600 hover:text-emerald-800'}`}
                        aria-label={`Take payment for ticket ${ticket.id}`}
                        disabled={!canCurrentUserProcessPayment}
                        title={!canCurrentUserProcessPayment ? "You do not have permission to process payments" : undefined}
                      >
                        Pay
                      </button>
                  )}
                   <button
                    onClick={() => onOpenPrintModal(`Device Label - ${ticket.id}`, <PrintableDeviceLabel ticket={ticket} />, 'label-content-area')}
                    className="text-slate-600 hover:text-[var(--color-primary)] font-medium transition-colors inline-flex items-center text-xs group"
                    aria-label={`Print label for ticket ${ticket.id}`}
                  >
                    <TagIconMini className="text-slate-600 group-hover:text-[var(--color-primary)]" /> Label
                  </button>
                  {onShowCustomerPortal && (
                    <button
                        onClick={() => onShowCustomerPortal(ticket.id)}
                        className="text-purple-600 hover:text-purple-800 font-medium transition-colors inline-flex items-center text-xs"
                        aria-label={`View customer portal for ${ticket.id}`}
                        title="View Customer Portal"
                    >
                        <EyeIconMini /> Portal
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
